"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { Truck, Package } from "lucide-react";

interface ManualShippingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    awb_code: string;
    courier_name: string;
    awb_file_url: string;
    tracking_url?: string;
  }) => void;
  loading?: boolean;
}

export function ManualShippingDetailsModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}: ManualShippingDetailsModalProps) {
  const [formData, setFormData] = useState({
    awb_code: "",
    courier_name: "",
    awb_file_url: "",
    tracking_url: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.awb_code.trim()) {
      newErrors.awb_code = "AWB code is required";
    }

    if (!formData.courier_name.trim()) {
      newErrors.courier_name = "Courier name is required";
    }

    if (!formData.awb_file_url.trim()) {
      newErrors.awb_file_url = "AWB file URL is required";
    } else if (!isValidUrl(formData.awb_file_url)) {
      newErrors.awb_file_url = "Please enter a valid URL";
    }

    if (formData.tracking_url && !isValidUrl(formData.tracking_url)) {
      newErrors.tracking_url = "Please enter a valid URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onSubmit({
      awb_code: formData.awb_code.trim(),
      courier_name: formData.courier_name.trim(),
      awb_file_url: formData.awb_file_url.trim(),
      tracking_url: formData.tracking_url.trim() || undefined,
    });
  };

  const handleClose = () => {
    setFormData({
      awb_code: "",
      courier_name: "",
      awb_file_url: "",
      tracking_url: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <Package className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              Enter Shipping Details
            </DialogTitle>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Provide the tracking details from your courier service
          </p>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <StandardFormInput
            label="AWB / Tracking Number"
            required
            value={formData.awb_code}
            onChange={(e) =>
              setFormData({ ...formData, awb_code: e.target.value })
            }
            placeholder="Enter AWB or tracking number"
            error={errors.awb_code}
            helperText="The tracking number provided by your courier"
          />

          <StandardFormInput
            label="Courier Name"
            required
            value={formData.courier_name}
            onChange={(e) =>
              setFormData({ ...formData, courier_name: e.target.value })
            }
            placeholder="e.g., Blue Dart, Delhivery, DTDC"
            error={errors.courier_name}
            helperText="Name of the courier service you're using"
          />

          <StandardFormInput
            label="AWB File URL"
            required
            value={formData.awb_file_url}
            onChange={(e) =>
              setFormData({ ...formData, awb_file_url: e.target.value })
            }
            placeholder="https://example.com/awb-label.pdf"
            error={errors.awb_file_url}
            helperText="Direct URL to the AWB/shipping label PDF"
          />

          <StandardFormInput
            label="Tracking URL"
            value={formData.tracking_url}
            onChange={(e) =>
              setFormData({ ...formData, tracking_url: e.target.value })
            }
            placeholder="https://example.com/track?awb=..."
            error={errors.tracking_url}
            helperText="Optional - Full tracking URL from courier website"
          />

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <Truck className="h-3 w-3 inline mr-1" />
              After submitting, the order set will be marked as shipped and the customer will be notified with the tracking details.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/60">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="text-sm h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="text-sm h-9 px-4 bg-primary hover:bg-primary/90"
          >
            {loading ? "Submitting..." : "Ship Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
